export type ActionType = 'simple' | 'timer' | 'counter'
export type ActionRepeatMode = 'repeatable' | 'once'

export interface Action {
  id: string
  areaId: string
  categoryIds: string[]
  objectiveIds: string[]
  name: string
  type: ActionType
  repeatMode: ActionRepeatMode
  xp: number
  coins: number
  completionCount: number
  totalAmount: number
  completedAt: number | null
  createdAt: number
  updatedAt: number
}

export interface ActionDraft {
  name: string
  type: ActionType
  repeatMode: ActionRepeatMode
  xp: number
  coins: number
  categoryIds: string[]
  objectiveIds: string[]
  newSpecializationName?: string | null
}

export interface ActiveAction {
  actionId: string
  areaId: string
  amount: number
  startedAt: number
}

export const amountForCompletion = (action: Action, amount: number) =>
  action.type === 'simple' ? 1 : Math.max(1, Math.trunc(amount))

export const rewardFor = (action: Action, amount: number) => {
  const multiplier = amountForCompletion(action, amount)
  return {
    xp: Math.min(2_147_483_647, action.xp * multiplier),
    coins: Math.min(2_147_483_647, action.coins * multiplier),
  }
}

export const actionStatsLabel = (action: Action, language: Language = 'es') => {
  const sessions = `${action.completionCount} ${language === 'es' ? (action.completionCount === 1 ? 'sesión' : 'sesiones') : (action.completionCount === 1 ? 'session' : 'sessions')}`
  if (action.type === 'simple') return `${action.completionCount} ${language === 'es' ? (action.completionCount === 1 ? 'vez' : 'veces') : (action.completionCount === 1 ? 'time' : 'times')}`
  if (action.type === 'counter') return `${action.totalAmount} reps · ${sessions}`
  return `${formatDuration(action.totalAmount)} · ${sessions}`
}

export const rewardRateLabel = (action: Action) => {
  const suffix = action.type === 'timer' ? '/min' : action.type === 'counter' ? '/rep' : ''
  return `+${action.xp} XP${suffix} · +${action.coins} 🪙${suffix}`
}

export const formatDuration = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes} min`
  if (minutes === 0) return `${hours} h`
  return `${hours} h ${minutes} min`
}

export const actionTypeLabel = (type: ActionType, language: Language = 'es') => {
  if (type === 'timer') return language === 'es' ? 'Tiempo' : 'Time'
  if (type === 'counter') return language === 'es' ? 'Contador' : 'Counter'
  return 'Simple'
}
import type { Language } from '../i18n'
